import {
    Checkbox,
    DatePicker,
    Flex,
    Form,
    Input,
    Modal,
    notification,
    Select,
} from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getMarketInfoCollection,
    getSchedulerAllSchedule,
    getTaskListsThunk,
    getWorkGetScheduleThunk,
    postFileUpload,
    postTaskCreateCustom,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    AssignmentResponseType,
    AssignmentType,
    Market,
    Option,
} from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { PhotoUploadInput } from "@/components/PhotoUploadField";
import { RichTextEditor } from "@/components/RichTextEditor";
import { PrimaryText } from "@/components/Typography/Styled";
import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import {
    ASSIGNMENT_RESPONSE_VOCABULARY,
    ASSIGNMENT_VOCABULARY,
} from "@/constants/ui/assignment";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { UploadPhotoItem } from "@/types/common";
import { ModalPayloads, ModalType } from "@/types/modals";

import { useAvailableDates } from "./hooks/useAvailableDates";
import { StyledCustomTaskForm } from "./Styled";

// ─── Constants ────────────────────────────────────────────────────────────────

const ASSIGNMENT_TYPE_OPTIONS = Object.values(
    ASSIGNMENT_VOCABULARY,
) as Option<AssignmentType>[];

const ASSIGNMENT_RESPONSE_OPTIONS = Object.values(
    ASSIGNMENT_RESPONSE_VOCABULARY,
) as Option<AssignmentResponseType>[];

const ALL_ROLES: Option<string>[] = [
    { label: "Мерчендайзер", value: "md" },
    { label: "Торговий агент", value: "ta" },
];

const FIXED_RESPONSE_TYPES: Partial<
    Record<AssignmentType, AssignmentResponseType>
> = {
    [AssignmentType.InfoCommunication]: AssignmentResponseType.Text,
};

const MESSAGES = {
    required: "Обов'язкове поле",
    minLength: "Мінімум 3 символи",
    uploadError: "Помилка завантаження файлу",
    createError: "Помилка створення доручення",
    createSuccess: "Доручення успішно створено",
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormValues {
    marketId: string;
    assignmentType: AssignmentType;
    assignmentResponse: AssignmentResponseType;
    roles: string[];
    title: string;
    visitDate: string;
    unplanned: boolean;
    description: string;
    photos: UploadPhotoItem[];
}

export interface CreateCustomTaskModalProps {
    payload: ModalPayloads[ModalType.CreateCustomTask];
    onCloseModal: () => void;
    onBack?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateCustomTaskModal: FC<CreateCustomTaskModalProps> = ({
    payload,
    onCloseModal,
    onBack,
}) => {
    const { market, images, agentId } = payload;
    const { role } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const { markets } = useAppSelector((s) => s.work);

    const dispatch = useAppDispatch();

    // В agentMode market з payload може бути відсутній —
    // активний маркет визначається вибором у дропдауні
    const isAgentMode = Boolean(agentId);

    const isRolesInitializedRef = useRef(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [routeMarkets, setRouteMarkets] = useState<Market[]>([]);
    const [isRouteMarketsLoading, setIsRouteMarketsLoading] = useState(false);

    // В звичайному режимі selectedMarketId = market.id і не змінюється
    // В agentMode — визначається вибором у формі
    const [selectedMarketId, setSelectedMarketId] = useState<
        string | undefined
    >(isAgentMode ? undefined : market?.id);

    const activeMarket = useMemo<Market | undefined>(() => {
        if (!isAgentMode) return market;
        return routeMarkets.find((m) => m.id === selectedMarketId);
    }, [isAgentMode, market, routeMarkets, selectedMarketId]);

    const today = useMemo(() => dayjs().startOf("day"), []);

    const initialPhotos = useMemo<UploadPhotoItem[]>(
        () => images?.map((url: string) => ({ kind: "url", url })) ?? [],
        [images],
    );

    // Передаємо undefined поки маркет не вибраний —
    // useAvailableDates не буде робити запит
    const {
        dates: availableDatesByRole,
        roles: availableRoleSlugs,
        isLoading: isLoadingDates,
    } = useAvailableDates(activeMarket?.id);

    const availableRoles = useMemo(
        () => ALL_ROLES.filter((r) => availableRoleSlugs.includes(r.value)),
        [availableRoleSlugs],
    );

    const [form] = Form.useForm<FormValues>();

    const [assignmentType, setAssignmentType] = useState<AssignmentType>(
        AssignmentType.Task,
    );
    const [roles, setRoles] = useState<string[]>([]);
    const [unplanned, setUnplanned] = useState(false);

    const isMultipleRoles = roles.length > 1;
    const mdWithoutDates = availableDatesByRole?.md?.length === 0;
    const isUnplannedForced = isMultipleRoles || mdWithoutDates;

    const availableDates = useMemo(() => {
        if (!roles.length) return [];
        return Array.from(
            new Set(roles.flatMap((r) => availableDatesByRole[r] ?? [])),
        );
    }, [roles, availableDatesByRole]);

    const availableDateSet = useMemo(
        () =>
            new Set(
                availableDates.map((d) =>
                    dayjs(d).format(backendDateFormat.date),
                ),
            ),
        [availableDates],
    );

    const firstValidDateIso = useMemo(() => {
        const sorted = availableDates
            .map((d) => dayjs(d).startOf("day"))
            .sort((a, b) => a.valueOf() - b.valueOf());
        const valid = sorted.find((d) => !d.isBefore(today));
        return (valid ?? today).toISOString();
    }, [availableDates, today]);

    // Завантаження маркетів агента
    useEffect(() => {
        if (!agentId) return;

        const fetchRouteMarkets = async () => {
            try {
                setIsRouteMarketsLoading(true);

                const { result: schedules } = await getSchedulerAllSchedule({
                    agentId,
                });

                const { result: marketsData } = await getMarketInfoCollection({
                    marketIds: schedules?.map((item) => item?.marketId),
                });

                setRouteMarkets(marketsData);
            } catch (error) {
                console.error(error);
            } finally {
                setIsRouteMarketsLoading(false);
            }
        };

        fetchRouteMarkets();
    }, [agentId]);

    // Скидаємо ролі і дату при зміні вибраного маркету
    useEffect(() => {
        if (!selectedMarketId) return;

        isRolesInitializedRef.current = false;
        setRoles([]);
        form.setFieldsValue({ roles: [], visitDate: today.toISOString() });
    }, [selectedMarketId, form, today]);

    // Ініціалізація ролей після завантаження дат
    useEffect(() => {
        const shouldSkip =
            isRolesInitializedRef.current ||
            isLoadingDates ||
            roles.length > 0 ||
            availableRoles.length === 0;

        if (shouldSkip) return;

        const initialRoles =
            availableRoles.length === 1
                ? [availableRoles[0].value]
                : role === "sv"
                  ? ["ta"]
                  : ["md"];

        form.setFieldValue("roles", initialRoles);
        setRoles(initialRoles);
        isRolesInitializedRef.current = true;
    }, [roles.length, availableRoles, isLoadingDates, form, role]);

    useEffect(() => {
        if (isUnplannedForced) {
            form.setFieldValue("unplanned", true);
            setUnplanned(true);
        }
    }, [isUnplannedForced, form]);

    useEffect(() => {
        const fixed = FIXED_RESPONSE_TYPES[assignmentType];
        if (fixed) form.setFieldValue("assignmentResponse", fixed);
    }, [assignmentType, form]);

    useEffect(() => {
        if (isLoadingDates || !roles.length) return;

        const current = dayjs(form.getFieldValue("visitDate")).startOf("day");
        const isPast = current.isBefore(today);
        const isAllowed = availableDateSet.has(
            current.format(backendDateFormat.date),
        );

        if (!unplanned && !isMultipleRoles && !mdWithoutDates) {
            if (isPast || !isAllowed)
                form.setFieldValue("visitDate", firstValidDateIso);
        } else if (isPast) {
            form.setFieldValue("visitDate", today.toISOString());
        }
    }, [
        unplanned,
        isMultipleRoles,
        mdWithoutDates,
        isLoadingDates,
        roles.length,
        availableDateSet,
        firstValidDateIso,
        today,
        form,
    ]);

    const shouldDisableDate = useCallback(
        (date: dayjs.Dayjs) => {
            if (date.isBefore(today)) return true;
            if (unplanned || isMultipleRoles || mdWithoutDates) return false;
            return !availableDateSet.has(date.format(backendDateFormat.date));
        },
        [today, unplanned, isMultipleRoles, mdWithoutDates, availableDateSet],
    );

    const uploadPhoto = useCallback(
        async (photo: UploadPhotoItem): Promise<string> => {
            if (photo.kind === "url") return photo.url;
            try {
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () =>
                        resolve((reader.result as string).split(",")[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(photo.file);
                });
                const { result } = await postFileUpload({
                    space: activeMarket!.id,
                    filename: photo.file.name,
                    base64Content: base64,
                });
                return result;
            } catch {
                notification.error({
                    message: `${MESSAGES.uploadError}: ${photo.file.name}`,
                });
                throw new Error();
            }
        },
        [activeMarket],
    );

    const handleFinish = async (values: FormValues) => {
        if (!activeMarket) return;

        setSubmitLoading(true);
        try {
            const files = await Promise.all(
                (values.photos ?? []).map(uploadPhoto),
            );
            await postTaskCreateCustom({
                name: values.title,
                description: values.description,
                targetDate: dayjs(values.visitDate).format(
                    backendDateFormat.date,
                ),
                targetId: activeMarket.id,
                targetType: "market",
                responseType: values.assignmentResponse,
                files,
                roles: values.roles,
                additionalData: { assignmentType: values.assignmentType },
            });

            notification.success({ message: MESSAGES.createSuccess });

            const marketIds = Object.keys(markets);
            const roleSlug = MANAGER_AGENT_VOCABULARY[role];

            if (activeSchedule === agentId || markets[activeMarket.id]) {
                await Promise.all([
                    dispatch(
                        getWorkGetScheduleThunk({
                            userId: activeSchedule,
                            roleSlug,
                        }),
                    ),
                    dispatch(
                        getTaskListsThunk({
                            targetIds: marketIds,
                            roleSlug,
                        }),
                    ),
                ]);
            }

            onCloseModal();
        } catch {
            notification.error({ message: MESSAGES.createError });
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleValuesChange = (changed: Partial<FormValues>) => {
        if ("marketId" in changed && changed.marketId)
            setSelectedMarketId(changed.marketId);
        if ("assignmentType" in changed)
            setAssignmentType(changed.assignmentType!);
        if ("roles" in changed) setRoles(changed.roles ?? []);
        if ("unplanned" in changed) setUnplanned(!!changed.unplanned);
    };

    const marketOptions = useMemo(
        () => routeMarkets.map((m) => ({ value: m.id, label: m.name })),
        [routeMarkets],
    );

    return (
        <Modal
            open
            title=""
            closable
            centered
            onCancel={onCloseModal}
            footer={null}
            width="100%"
            style={{ maxWidth: 400 }}
            destroyOnClose
        >
            <PrimaryText
                textAlign="center"
                fontSize={16}
                fontWeight="500"
                padding="0 20px 0 0"
            >
                {isAgentMode
                    ? "Створення доручення"
                    : `Створення доручення для ТТ "${market?.name}"`}
            </PrimaryText>

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={handleFinish}
                onValuesChange={handleValuesChange}
                initialValues={{
                    visitDate: today.toISOString(),
                    assignmentType: AssignmentType.Task,
                    assignmentResponse: AssignmentResponseType.Bool,
                    title: "",
                    description: "",
                    roles: [],
                    unplanned: false,
                    photos: initialPhotos,
                }}
            >
                <StyledCustomTaskForm>
                    {isAgentMode && (
                        <Form.Item
                            name="marketId"
                            label="Торгова точка"
                            rules={[
                                { required: true, message: MESSAGES.required },
                            ]}
                        >
                            <Select
                                options={marketOptions}
                                placeholder="Оберіть торгову точку"
                                loading={isRouteMarketsLoading}
                                disabled={isRouteMarketsLoading}
                                showSearch
                                size="large"
                                optionRender={(option) => (
                                    <span
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {option.label}
                                    </span>
                                )}
                                filterOption={(input, option) =>
                                    (option?.label ?? "")
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="assignmentType"
                        label="Тип доручення"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <Select
                            options={ASSIGNMENT_TYPE_OPTIONS}
                            placeholder="Оберіть тип"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="assignmentResponse"
                        label="Тип відповіді"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <Select
                            options={ASSIGNMENT_RESPONSE_OPTIONS}
                            placeholder="Оберіть тип відповіді"
                            disabled={assignmentType in FIXED_RESPONSE_TYPES}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="title"
                        label="Назва доручення"
                        rules={[
                            { required: true, message: MESSAGES.required },
                            { min: 3, message: MESSAGES.minLength },
                        ]}
                    >
                        <Input
                            placeholder="Введіть назву доручення"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="roles"
                        label="Ролі"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <Select
                            mode="multiple"
                            options={availableRoles}
                            placeholder="Оберіть ролі"
                            // Блокуємо поки маркет не вибраний або дати ще вантажаться
                            disabled={!activeMarket || isLoadingDates}
                            loading={isLoadingDates}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="visitDate"
                        label="Дата"
                        rules={[{ required: true, message: MESSAGES.required }]}
                        getValueProps={(v) => ({ value: v ? dayjs(v) : null })}
                        getValueFromEvent={(d: dayjs.Dayjs | null) =>
                            d?.toISOString() ?? undefined
                        }
                    >
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Оберіть дату"
                            minDate={today}
                            disabledDate={shouldDisableDate}
                            allowClear={false}
                            // Блокуємо поки маркет не вибраний або дати ще вантажаться
                            disabled={!activeMarket || isLoadingDates}
                            format={(val) =>
                                `${val.format("D MMMM YYYY")} року`
                            }
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="unplanned"
                        valuePropName="checked"
                        style={{ marginTop: -8 }}
                    >
                        <Checkbox disabled={isUnplannedForced}>
                            Позапланово
                        </Checkbox>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Опис"
                        rules={[{ required: true, message: MESSAGES.required }]}
                    >
                        <RichTextEditor
                            placeholder="Введіть опис"
                            name="description"
                        />
                    </Form.Item>

                    <Form.Item name="photos" label="Фото">
                        <PhotoUploadInput buttonLabel="Додати фото" />
                    </Form.Item>
                </StyledCustomTaskForm>

                <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
                    <Flex gap={8} justify="flex-end">
                        {onBack && (
                            <BaseButton
                                onClick={onBack}
                                disabled={submitLoading}
                            >
                                Повернутися назад
                            </BaseButton>
                        )}
                        <BaseButton
                            type="primary"
                            htmlType="submit"
                            loading={submitLoading}
                            disabled={submitLoading}
                        >
                            Створити
                        </BaseButton>
                    </Flex>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateCustomTaskModal;
